import { Button, Col, Row } from "antd";
import { Controller, useForm } from "react-hook-form";
import InputBox from "../../../components/InputBox";
import SelectBox from "../../../components/SelectBox";
import { User, UsersResponse } from "./UsersTab";
import apiService from "../../../services/apiService";
import { useEffect, useState } from "react";
import { Role, RolesResponse } from "./RolesTab";
import { getLocalStorageItem } from "../../../utils/Validation";
import { toast } from "react-toastify";
import { UserRound, Mail, Phone, UsersRound, ShieldHalf } from "lucide-react";

interface AddLeadFormProps {
  closeModal: () => void;
  isEdit?: boolean;
  id?: string | number;
  getUser?: (user: User[]) => void;
}

interface AddUserFormInputs {
  name: string;
  mobile: string;
  email: string;
  role: string;
  reportingManager: string[];
}

const AddUserForm = ({
  isEdit = false,
  closeModal,
  id = "",
  getUser,
}: AddLeadFormProps) => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);

  const [edittedUser, setEdittedUser] = useState<any>();

  const onSubmit = async (data: AddUserFormInputs) => {
    const splitFullName = (fullName: string) => {
      const nameParts = fullName.trim().split(" ");
      return {
        firstName: nameParts[0],
        lastName: nameParts.length > 1 ? nameParts.slice(1).join(" ") : " ",
      };
    };

    const { firstName, lastName } = splitFullName(data.name);

    // Convert reporting manager names to IDs
    const reportingManagerIds = data.reportingManager
      .map((name) => {
        const manager = users.find(
          (user) => `${user.firstName} ${user.lastName}` === name
        );
        return manager?.id;
      })
      .filter((id) => id !== undefined);

    const payload = {
      firstName: firstName,
      lastName: lastName,
      mobile: data.mobile,
      email: data.email,
      roleId: data.role,
      reportingManagerIds: reportingManagerIds,
    };
    let errorResponse = "";
    try {
      let response: any;
      if (isEdit && id) {
        response = await apiService.put(`/users/edit-user/${id}`, payload);
      } else {
        response = await apiService.post("/users/create-user", {
          ...payload,
          status: true,
          password: "",
        });
      }

      if (response.message === "User created successfully and email sent.") {
        toast.success(response.message);
        reset();
        closeModal();
        closeModal();
        await fetchUsers();
      }
      if (response.message === "User updated successfully.") {
        toast.success(response.message);
        reset();
        closeModal();
        await fetchUsers();
      }

      errorResponse = response.message;
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error((error as any)?.message || "An error occurred");
    }
  };

  const fetchUsers = async () => {
    try {
      const response: UsersResponse = await apiService.get(`/users`);
      if (response.users) {
        setUsers(response.users);
        getUser && getUser(response.users);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchUserById = async (id: string | number) => {
    try {
      const response: any = await apiService.get(`/users/${id}`);
      if (response) {
        setEdittedUser(response);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    if (isEdit && id && !isNaN(Number(id))) {
      fetchUserById(id);
    }
  }, [isEdit, id]);

  const usersData = getLocalStorageItem("detail");

  const fetchRoles = async () => {
    try {
      const response: RolesResponse = await apiService.get(
        `/roles?userId=${usersData.userId}`
      );
      if (response.roles) {
        setRoles(response.roles);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const reportingManagers = users.map((user) => ({
    value: `${user.firstName} ${user.lastName}`,
    label: `${user.firstName} ${user.lastName}`,
  }));

  const rolesOptions = roles.map((user) => ({
    value: user.id,
    label: `${user.role_name}`,
  }));
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<AddUserFormInputs>();

  const onClickOfCancel = () => {
    reset();
    closeModal();
  };

  useEffect(() => {
    if (isEdit && edittedUser) {
      reset({
        name: edittedUser.lastName?.trim()
          ? `${edittedUser.firstName} ${edittedUser.lastName}`
          : edittedUser.firstName,
        mobile: edittedUser.mobile && edittedUser.mobile.toString(),
        email: edittedUser.email,
        role: edittedUser.role?.id || "",
        reportingManager:
          edittedUser.reportingManagers?.map((manager: any) => `${manager}`) ||
          [],
      });
    }
  }, [isEdit, edittedUser, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Controller
            name="name"
            control={control}
            rules={{
              required: "Name is required",
              pattern: {
                value: /^[A-Za-z]+( [A-Za-z]+)*$/,
                message:
                  "Name can only contain letters and spaces. No special characters or numbers allowed.",
              },
              validate: {
                noSpecialCharsOrNumbers: (value) =>
                  /^[A-Za-z]+( [A-Za-z]+)*$/.test(value.trim()) ||
                  "Name can only contain letters and spaces. No special characters or numbers allowed.",
              },
              minLength: {
                value: 3,
                message: "Name must be at least 3 characters long",
              },
            }}
            render={({ field }) => (
              <InputBox
                {...field}
                placeholder="Enter Name"
                label="Name"
                prefix={<UserRound size={16} strokeWidth={2.2} />}
                error={errors.name?.message}
                disabled={isEdit}
              />
            )}
          />
        </Col>
        <Col span={12}>
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Enter a valid email address",
              },
            }}
            render={({ field }) => (
              <InputBox
                {...field}
                placeholder="Email"
                label="Email"
                prefix={<Mail size={16} strokeWidth={2.2} />}
                error={errors.email?.message}
                disabled={isEdit}
              />
            )}
          />
        </Col>
        <Col span={12}>
          <Controller
            name="mobile"
            control={control}
            rules={{
              required: "Mobile is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Enter a valid 10-digit mobile number",
              },
            }}
            render={({ field }) => (
              <InputBox
                {...field}
                placeholder="Enter 10-digit mobile number"
                label="Mobile"
                prefix={<Phone size={16} strokeWidth={2.2} />}
                error={errors.mobile?.message}
                disabled={isEdit}
              />
            )}
          />
        </Col>
        <Col span={12}>
          <Controller
            name="reportingManager"
            control={control}
            defaultValue={[]} // Add default value as empty array
            rules={{ required: "Reporting manager is required" }}
            render={({ field }) => (
              <SelectBox
                {...field}
                value={field.value || []} // Ensure value is always an array
                onChange={(value) => field.onChange(value)} // Explicitly handle onChange
                label="Reporting Manager"
                placeholder="Select Reporting manager"
                prefix={<UsersRound size={16} strokeWidth={2.2} />}
                error={errors.reportingManager?.message}
                options={reportingManagers}
                mode="multiple"
              />
            )}
          />
        </Col>
        <Col span={12}>
          <Controller
            name="role"
            control={control}
            rules={{ required: "Role is required" }}
            render={({ field }) => (
              <SelectBox
                {...field}
                label="Role"
                placeholder="Select Role"
                prefix={<ShieldHalf size={16} strokeWidth={2.2} />}
                error={errors.role?.message}
                options={rolesOptions}
              />
            )}
          />
        </Col>
      </Row>

      <div style={{ 
        marginTop: "24px", 
        paddingTop: "20px", 
        borderTop: "1px solid #f1f5f9", 
        display: "flex", 
        justifyContent: "flex-end", 
        gap: "12px" 
      }}>
        <Button 
          onClick={() => onClickOfCancel()}
          style={{
            height: '42px',
            borderRadius: '12px',
            paddingInline: '24px',
            fontSize: '14px',
            fontWeight: 500,
            transition: 'all 0.2s'
          }}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          style={{
            height: '42px',
            borderRadius: '12px',
            paddingInline: '28px',
            backgroundColor: '#3f8b98',
            boxShadow: '0 8px 16px rgba(63, 139, 152, 0.2)',
            fontSize: '14px',
            fontWeight: 600,
            border: 'none',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e: any) => e.currentTarget.style.backgroundColor = '#4d9aa6'}
          onMouseLeave={(e: any) => e.currentTarget.style.backgroundColor = '#3f8b98'}
        >
          {isEdit ? "Save Changes" : "Save User"}
        </Button>
      </div>
    </form>
  );
};

export default AddUserForm;
